const MIME_TYPE_PDF = 'application/pdf';
const MIME_TYPE_PPT = 'application/vnd.ms-powerpoint';
const MIME_TYPE_PPTX =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const MIME_TYPE_DEFAULT = 'application/octet-stream';

type SlideExtension = 'ppt' | 'pptx';
type DetectedSlideFormat = SlideExtension | 'unknown';

const getFileExtension = (filename: string): string => {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === filename.length - 1) {
    return '';
  }
  return filename.slice(dotIndex + 1).toLowerCase();
};

const replaceFileExtension = (
  filename: string,
  extension: SlideExtension,
): string => {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1) {
    return `${filename}.${extension}`;
  }
  return `${filename.slice(0, dotIndex)}.${extension}`;
};

const createFileWithMetadata = (
  file: File,
  filename: string,
  type: string,
): File =>
  new File([file], filename, {
    type,
    lastModified: file.lastModified,
  });

const readBlobBytes = async (blob: Blob): Promise<Uint8Array | null> => {
  if (typeof blob.arrayBuffer === 'function') {
    try {
      return new Uint8Array(await blob.arrayBuffer());
    } catch {
      return null;
    }
  }

  return new Promise(resolve => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
        return;
      }
      resolve(null);
    };

    reader.onerror = () => {
      resolve(null);
    };

    reader.readAsArrayBuffer(blob);
  });
};

const detectSlideFormatBySignature = async (
  file: File,
): Promise<DetectedSlideFormat> => {
  const header = await readBlobBytes(file.slice(0, 8));
  if (!header) {
    return 'unknown';
  }

  // PK: ZIP header used by OOXML (.pptx) files
  if (header.length >= 2 && header[0] === 0x50 && header[1] === 0x4b) {
    return 'pptx';
  }

  // D0 CF 11 E0: Compound File Binary header used by legacy .ppt files
  if (
    header.length >= 4 &&
    header[0] === 0xd0 &&
    header[1] === 0xcf &&
    header[2] === 0x11 &&
    header[3] === 0xe0
  ) {
    return 'ppt';
  }

  return 'unknown';
};

export const normalizeSlideFileForUpload = async (
  file: File,
): Promise<File> => {
  const extension = getFileExtension(file.name);
  if (extension !== 'ppt' && extension !== 'pptx') {
    return file;
  }

  const detectedFormat = await detectSlideFormatBySignature(file);

  if (extension === 'ppt' && detectedFormat === 'pptx') {
    return createFileWithMetadata(
      file,
      replaceFileExtension(file.name, 'pptx'),
      MIME_TYPE_PPTX,
    );
  }

  if (extension === 'pptx' && detectedFormat === 'ppt') {
    return createFileWithMetadata(
      file,
      replaceFileExtension(file.name, 'ppt'),
      MIME_TYPE_PPT,
    );
  }

  if (extension === 'ppt' && file.type !== MIME_TYPE_PPT) {
    return createFileWithMetadata(file, file.name, MIME_TYPE_PPT);
  }

  if (extension === 'pptx' && file.type !== MIME_TYPE_PPTX) {
    return createFileWithMetadata(file, file.name, MIME_TYPE_PPTX);
  }

  return file;
};

export const getSlideContentType = (file: File): string => {
  if (file.type) {
    return file.type;
  }

  const extension = getFileExtension(file.name);
  if (extension === 'pdf') return MIME_TYPE_PDF;
  if (extension === 'ppt') return MIME_TYPE_PPT;
  if (extension === 'pptx') return MIME_TYPE_PPTX;
  return MIME_TYPE_DEFAULT;
};
